// src/data/albertaRecoveryModel.js
//
// THE ALBERTA RECOVERY MODEL as a data module, for the executive console's
// optional "Alberta Recovery Model" tab (enterprise/RecoveryModelPanel.jsx,
// switched on per organization with features.enableRecoveryModel).
//
// The Government of Alberta thinks in a continuum (prevention, intervention,
// treatment, recovery), five key focus areas and five key principles, and the
// ministry is held to the four outcomes of its 2025-28 business plan. This
// module carries that frame, in the government's own words where it quotes
// them, and maps the console's EXISTING figures onto it. It adds no figure of
// its own. Government wording comes from alberta.ca (the Alberta Recovery
// Model page), the Ministry of Mental Health and Addiction Business Plan
// 2025-28 and the Canadian Centre of Recovery Excellence 2025-26 Annual
// Report, and from nowhere else. The `eas` sentences are ours and claim only
// what the console does: EAS delivers no treatment; it sits in front of the
// services the province funds and routes people to them, counted in aggregate.
// No partnership, endorsement or data connection with any government body is
// claimed, and the UI must never imply one.
//
// Edition overrides (all optional, nothing breaks when absent):
//   edition.recoveryModel = {
//     lead: 'youth',                       // the focus area this edition opens with
//     notes: { youth: 'sentence' },        // replaces an area's generic `eas` text
//     objectives: ['3.2', '3.5']           // business-plan refs to list first
//   }

// Dictionary ids the Treatment and Recovery stages look for among the
// organization's own figures (live.business / live.businessByGroup, or a typed
// executive input). Ids not yet in workforceDictionary.js render by id until
// the dictionary carries them; ids are keys and never change.
export const TREATMENT_METRIC_IDS = [
    'efap_utilization_rate', 'counselling_intakes', 'counselling_wait_days', 'helpline_contacts',
    'psychology_claims_per_100', 'students_seen', 'counsellor_referrals', 'mh_access_share'
];
export const RECOVERY_METRIC_IDS = [
    'absence_days_per_fte', 'absence_rate', 'modified_duty_episodes', 'turnover_voluntary_rate',
    'std_claims_opened', 'attendance_rate', 'chronic_absence_share'
];

// The continuum, in the government's order. `reads` are the console figures
// the panel renders under each stage (resolved in RecoveryModelPanel.jsx).
export const CONTINUUM = [
    {
        id: 'prevention',
        label: 'Prevention',
        eas: 'A private check-in before anything is wrong: who is taking part, and how many stand well.',
        tab: 'cc-overview',
        reads: [
            { id: 'responded', label: 'Members with a check-in' },
            { id: 'participation', label: 'Participation' },
            { id: 'well_share', label: 'Thriving or building' }
        ]
    },
    {
        id: 'intervention',
        label: 'Intervention',
        eas: 'Where strain concentrates, early enough to act, with the next step one tap away.',
        tab: 'cc-readiness',
        reads: [
            { id: 'running_hot', label: 'Running hot' },
            { id: 'needs_support', label: 'Needs support' },
            { id: 'strain_groups', label: 'Strain concentrates in' },
            { id: 'population_risk', label: 'At risk on the screen' }
        ]
    },
    {
        id: 'treatment',
        label: 'Treatment',
        eas: 'EAS delivers no treatment. It routes to the services the province and the organization already fund, and shows their own figures beside the check-ins.',
        tab: 'cc-data',
        reads: [
            { id: 'org_figures', label: 'On file', metrics: TREATMENT_METRIC_IDS }
        ]
    },
    {
        id: 'recovery',
        label: 'Recovery',
        eas: 'Movement from the first reading, shown for groups, never for a person.',
        tab: 'cc-triangulation',
        reads: [
            { id: 'baseline_movement', label: 'Movement from baseline' },
            { id: 'org_figures', label: 'On file', metrics: RECOVERY_METRIC_IDS },
            { id: 'fixed_line', label: 'Recovery is expected: the console shows movement, never a person.' }
        ]
    }
];

// The five key focus areas, titles exactly as the Government of Alberta
// publishes them, in its order. `government` facts are paraphrased closely
// from the published page; every number in them is the government's.
// `signals`: snapshot paths and dictionary ids that evidence the area in the
// console (`leadSnapshot` counts only when the area is the edition's lead).
export const FOCUS_AREAS = [
    {
        id: 'access',
        title: 'Increasing access to treatment',
        government: [
            'Added more than 10,000 publicly funded mental health and addiction treatment spaces across the province, a 55% increase in capacity within the system. More than 29,000 Albertans can now access treatment annually.',
            'Adding 60 community-based mental health and addiction beds to give patients a safe place to continue treatment and recovery when they no longer need acute care.'
        ],
        eas: 'EAS adds no treatment space. It sits in front of them: a private check-in that points a person to the publicly funded service that fits, and counts that contact in aggregate so a program can see where the need is.',
        signals: {
            snapshot: ['participation.responded', 'participation.rate'],
            metrics: ['efap_utilization_rate', 'counselling_intakes', 'helpline_contacts', 'mh_access_share']
        },
        tab: 'cc-overview'
    },
    {
        id: 'barriers',
        title: 'Removing barriers to care',
        government: [
            'Eliminated daily user fees for publicly funded addiction treatment spaces. A 90-day live-in program previously cost $3,600 at $40 per day.',
            'Supported police to help Albertans experiencing mental health and addiction challenges, including the digital HealthIM tool and teams trained to connect individuals to recovery-oriented services.'
        ],
        eas: 'The check-in is free to the member, private by design and works on a phone. The province\'s own lines (211, 9-8-8, the AHS helplines) are one tap away in the hub. No individual answer ever reaches staff.',
        signals: {
            snapshot: ['participation.rate', 'quadrants.needs_support'],
            metrics: ['counselling_wait_days', 'helpline_contacts']
        },
        tab: 'cc-readiness'
    },
    {
        id: 'facilities',
        title: 'Building new facilities',
        government: [
            'Committed up to $350 million to build 11 recovery communities that offer holistic live-in addiction treatment. Once operational they will add more than 700 long-term addiction treatment beds.',
            'Five of these recovery communities are being built in partnership with Indigenous communities, incorporating Indigenous ways of healing, knowing and doing.'
        ],
        eas: 'Building is not ours. A hub is built to carry a community\'s own path to its recovery community and the aggregate picture of need around it, by group or region, at five or more.',
        signals: {
            snapshot: ['groups'],
            metrics: []
        },
        tab: 'cc-triangulation'
    },
    {
        id: 'immediate',
        title: 'Making treatment immediately available',
        government: [
            'Expanded affordable counselling options, virtual and in-person, through a partnership with Counselling Alberta. There is no wait list, and Albertans pay what they can afford.',
            'Increased funding for 211 Alberta to enhance navigation services for rural and underserved communities.'
        ],
        eas: 'A check-in ends the same day with a next step: 211, 9-8-8, the helplines and the organization\'s own program, with the number in the member\'s hand. No wait and no referral to begin a check-in.',
        signals: {
            snapshot: ['quadrants.running_hot', 'quadrants.needs_support', 'gambling.riskShare'],
            metrics: ['counselling_wait_days', 'counselling_intakes', 'counsellor_referrals']
        },
        tab: 'cc-readiness'
    },
    {
        id: 'youth',
        title: 'Building better futures for our youth',
        government: [
            'Expanded prevention and early intervention supports for youth aged 11 to 25 at Kickstand Centres across Alberta, with free and confidential mental health and addiction services.',
            'Supported school-based mental health programs, including the CASA Mental Health Classroom Program for students in grades 4 to 12 and the Integrated School Support Program.'
        ],
        eas: 'Members under 18 check in with guardian consent and a content floor. Parents get a check-in about their own child and talk guides, never the child\'s answers. A school or a team sees aggregates only, at groups of five or more.',
        signals: {
            snapshot: [],
            leadSnapshot: ['participation.responded', 'participation.rate'],
            metrics: ['students_seen', 'counsellor_referrals', 'attendance_rate', 'chronic_absence_share']
        },
        tab: 'cc-data'
    }
];

// The ministry's four outcomes, from the Ministry of Mental Health and
// Addiction Business Plan 2025-28. Statements, objectives and measures are
// quoted as published; only the objectives the console informs are listed.
// Our `eas` lines inform a measure or are built for it; they never move one.
export const OUTCOMES = [
    {
        id: 'system',
        title: 'Outcome 1: System',
        statement: 'Alberta has an integrated and comprehensive mental health and addiction system that enables access to a robust continuum of supports and services to help individuals achieve recovery and wellness.',
        objectives: [
            {
                ref: '1.4',
                text: 'Continue to build an engaged, highly skilled mental health and addiction workforce and improve knowledge, expertise, and capacity to provide appropriate supports and deliver exceptional care across Alberta.',
                eas: 'The staff side: readiness and strain for the people who deliver care and public services, by group, at five or more.'
            },
            {
                ref: '1.5',
                text: 'Improve accessibility of mental health and addiction treatment services in police and correctional facilities and continue to advance treatment referral pathways and supports for people who interact with the justice system to facilitate recovery.',
                eas: 'For police officers and corrections staff themselves: a private check-in and a route to the service that fits, counted in aggregate.'
            }
        ],
        measures: [
            {
                ref: '1.a',
                kind: 'Performance measure',
                name: 'Spectrum of Care',
                description: 'percentage of individuals admitted for more than 30 days to hospital in-patient care for a mental health issue',
                baseline: '14.1% in 2023-24',
                targets: '11.8% by 2025-26, 10.8% by 2026-27, 9.8% by 2027-28',
                eas: 'Informed indirectly: the console shows where strain concentrates in a population before it reaches a hospital, in aggregate only.'
            }
        ]
    },
    {
        id: 'individual',
        title: 'Outcome 2: Individual',
        statement: 'Albertans have timely, appropriate, and consistent access to a continuum of high-quality, person-centred mental health and addiction supports to meet their unique needs.',
        objectives: [
            {
                ref: '2.1',
                text: 'Continue to develop and implement information technology infrastructure, including the My Recovery Plan platform, to monitor the addiction treatment system and changes in Albertans\' recovery capital while ensuring their privacy is fully protected.',
                eas: 'EAS is not My Recovery Plan and does not replace it. It measures people outside treatment, in the community, at work and at school, with privacy built in: groups of five or more, aggregates only.'
            },
            {
                ref: '2.2',
                text: 'Expand access to and promote virtual, phone, and in-person mental health and addiction supports and services to improve health outcomes for more Albertans, including underserved communities and rural and remote populations.',
                eas: 'A phone is enough. The check-in and the next step work anywhere in the province, and a hub shows reach by region so a program can see where it is not landing.'
            },
            {
                ref: '2.4',
                text: 'Provide a coordinated range of programs and services for people with mental health needs of varying complexity to develop coping skills, improve well-being, and support better health outcomes.',
                eas: 'The member side: topic content, the AI Coach and recovery tools matched to what the check-in found, with the province\'s lines one tap away.'
            },
            {
                ref: '2.5',
                text: 'Expand and deliver coordinated supports for individuals to help build recovery capital and sustain long-term recovery.',
                eas: 'Movement from baseline over time, shown for groups, never for a person: the same logic as recovery capital, for people who are not in treatment.'
            }
        ],
        measures: [
            {
                ref: '2.a',
                kind: 'Performance indicator',
                name: 'Personal improvement',
                description: 'change in recovery capital scores of Albertans in publicly funded addiction treatment on the My Recovery Plan platform',
                baseline: '43% increase in 2023-24',
                targets: null,
                eas: 'The same logic for people who are not in treatment: movement from a member\'s own baseline in readiness and wellbeing, shown in aggregate, privacy protected by design. EAS is not My Recovery Plan and does not replace it.'
            }
        ]
    },
    {
        id: 'community',
        title: 'Outcome 3: Community',
        statement: 'Alberta\'s communities are engaged and have the capacity and resources to support families and help individuals build recovery capital and lead satisfying, hopeful, and contributing lives.',
        objectives: [
            {
                ref: '3.2',
                text: 'Expand access and improve mental health supports for children and youth in schools and in community to promote positive mental health and prevent severe mental health issues from developing.',
                eas: 'Built for a school division: a youth check-in with guardian consent and a content floor, aggregates by school at five or more, and the parent layer beside it.'
            },
            {
                ref: '3.3',
                text: 'Partner with First Nations, Métis, and Inuit communities to continue developing a comprehensive continuum of culturally relevant mental health and addiction services to support Indigenous peoples in Alberta.',
                eas: 'A community wellness hub in the community\'s own voice, pointing to its own services and the province\'s. Figures are shown in aggregate only.'
            },
            {
                ref: '3.4',
                text: 'Expand recovery-oriented initiatives and interventions that connect people experiencing mental health and addiction challenges with personalized and effective supports.',
                eas: 'The check-in ends with a next step that fits the person, and the console shows in aggregate where people stand so a program can place its effort.'
            },
            {
                ref: '3.5',
                text: 'Support and deliver targeted prevention and intervention initiatives that promote mental health resiliency for individuals, families, and communities.',
                eas: 'Prevention and intervention for individuals, for families through the parent layer, and for communities through the hub, with the console reading all three in one vocabulary.'
            }
        ],
        measures: [
            {
                ref: '3.a',
                kind: 'Performance measure',
                name: 'Community based access',
                description: 'mental health and addiction-related emergency department visits with no interaction with publicly funded mental health and addiction services in the previous two fiscal years',
                baseline: '19.7% in 2023-24',
                targets: '16.9% by 2025-26, 16.1% by 2026-27, 15.4% by 2027-28',
                eas: 'The measure EAS speaks to most directly: a first contact in the community, at work or at school, counted in aggregate, that points people to publicly funded services before an emergency department does.'
            }
        ]
    },
    {
        id: 'excellence',
        title: 'Outcome 4: Excellence',
        statement: 'Albertans experience effective, high-quality mental health and addiction care grounded in continuous improvement, innovation, and informed decision making.',
        objectives: [
            {
                ref: '4.1',
                text: 'Conduct system-level evaluation of Alberta\'s recovery-oriented system of care and research through CoRE to inform the delivery of mental health and addiction services and improve recovery outcomes for Albertans.',
                eas: 'Aggregate figures in a controlled vocabulary, every one labelled with its source, in a form a system evaluator can use. EAS claims no connection to any evaluator.'
            },
            {
                ref: '4.2',
                text: 'Review and evaluate processes, programs, and services and continue to refine tools to improve the data and evidence available to inform decision making and policy development to advance the Alberta Recovery Model.',
                eas: 'Three labelled legs under every figure: what members report, what the organization\'s own systems say, and a sector reference. The definition questions travel with each figure.'
            }
        ],
        measures: [
            {
                ref: '4.a',
                kind: 'Performance indicator',
                name: 'Efficacy of care',
                description: 'percentage of individuals admitted three or more times to a hospital under the Mental Health Act',
                baseline: '8.2% in 2023-24',
                targets: null,
                eas: 'A system indicator the console does not touch. What it offers an evaluator is aggregate, controlled-vocabulary figures with the source on every one.'
            }
        ]
    }
];

// The model's five key principles (CoRE 2025-26 Annual Report), each with
// one line on how the console honours it.
export const PRINCIPLES = [
    { id: 'accessible', label: 'Accessible', eas: 'A private check-in on a phone, anywhere in the province, with the next step in the member\'s hand.' },
    { id: 'coordinated', label: 'Coordinated', eas: 'Routing to the services the province already funds, counted in aggregate.' },
    { id: 'evidence', label: 'Evidence Based', eas: 'Three labelled legs under every figure: members, the organization\'s own systems, a sector reference.' },
    { id: 'cross_sectoral', label: 'Cross Sectoral', eas: 'Figures from HR, payroll, programs and schools in one controlled vocabulary.' },
    { id: 'recovery_expected', label: 'Recovery is Expected', eas: 'Movement from baseline, shown for groups, never for a person.' }
];

export const ATTRIBUTION = 'Focus areas and principles as published by the Government of Alberta for the Alberta Recovery Model. EAS maps its own figures to them. This is not a government report and implies no affiliation.';

export const SOURCE_NOTE = 'alberta.ca, the Alberta Recovery Model; Canadian Centre of Recovery Excellence 2025-26 Annual Report';

export const PLAN_NOTE = 'From the ministry\'s 2025-28 business plan';

export const PRIVACY_LINE = 'Groups of five or more, complementary suppression, aggregates computed on the server, hosted in Montréal. Nothing on this page is about a person.';

// The focus area an edition opens with, and its note for an area, when set.
export const leadAreaFor = (edition) => {
    const lead = edition && edition.recoveryModel && edition.recoveryModel.lead;
    return FOCUS_AREAS.some((a) => a.id === lead) ? lead : null;
};
export const areaNoteFor = (edition, areaId) => {
    const notes = edition && edition.recoveryModel && edition.recoveryModel.notes;
    return notes && typeof notes[areaId] === 'string' && notes[areaId].trim() ? notes[areaId] : null;
};
